import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { highRiskApi } from '../api/highRiskApi';
import { mapBackendToForm, mapFormToBackend, validateHighRiskForm } from '../utils/mapping';
import { useNotification } from '../../../../contexts/NotificationContext';
import { signatureDatetimeMap } from '../constants/options';
import { clearDraft } from '../../../../utils/draftUtils';
import { DRAFT_KEYS } from '../../../../utils/draftUtils';

export const useHighRiskForm = (uhid, navigate) => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid || "",
    hospital_id: 1,
    procedure_by_dr: "",
    primary_disease: "",
    associated_risk_factor: "",
    complications: "",
    patient_signature: "",
    patient_name: "",
    patient_signature_date_time: "",
    witness_signature: "",
    witness_name: "",
    witness_signature_date_time: "",
    doctor_anaesthetist_signature: "",
    doctor_anaesthetist_patient_name: "",
    doctor_anaesthetist_signature_date_time: "",
    interpreter_signature: "",
    interpreter_name: "",
    interpreter_signature_date_time: "",
    unable_consent_reason: "",
    attender_name: "",
    patient_rep_signature: "",
    patient_rep_patient_name: "",
    patient_rep_signature_date_time: "",
    witness_signature_unable_consent: "",
    witness_name_unable_consent: "",
    witness_signature_unable_consent_date_time: "",
    doctor_signature_unable_consent: "",
    doctor_signature_unable_consent_patient_name: "",
    doctor_signature_unable_consent_date_time: "",
    interpreter_signature_unable_consent: "",
    interpreter_name_unable_consent: "",
    interpreter_signature_unable_consent_date_time: ""
  });

  const [errors, setErrors] = useState({});
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const [isExistingRecord, setIsExistingRecord] = useState(false);

  // Fetch initial data
  const { data: fetchedData, isLoading: isInitialLoading } = useQuery({
    queryKey: ['highRiskConsent', uhid],
    queryFn: async () => {
      try {
        const [patientData, ipData] = await Promise.all([
          highRiskApi.getPatient(uhid),
          highRiskApi.getNextIp()
        ]);

        let consentData = {};
        try {
          consentData = await highRiskApi.getConsent(uhid);
        } catch (e) {
          console.log("No existing consent found, starting fresh.");
        }

        return { patientData, consentData, ipData };
      } catch (error) {
        showNotification('error', 'Failed to fetch patient information');
        throw error;
      }
    },
    enabled: !!uhid,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!fetchedData) return;

    const { patientData, consentData, ipData } = fetchedData;

    if (consentData && Object.keys(consentData).length > 0) {
      setIsExistingRecord(true);
    } else {
      setIsExistingRecord(false);
    }

    const mappedData = mapBackendToForm(consentData);
    
    setFormData(prev => ({
      ...prev,
      ...mappedData,
      uhid_no: uhid,
      patient_name: patientData?.name || mappedData.patient_name || '',
      ip_no: mappedData.ip_no || ipData?.next_ip_no || prev.ip_no
    }));
  }, [fetchedData, uhid]);

  // Transcription Mutation
  const transcriptionMutation = useMutation({
    mutationFn: highRiskApi.transcribeAudio,
    onSuccess: (data) => {
      setFormData(prev => ({ ...prev, primary_disease: data.transcription }));
      showNotification('success', 'Audio transcribed successfully');
    },
    onError: () => showNotification('error', 'Transcription failed')
  });

  // Submit Mutation
  const submitMutation = useMutation({
    mutationFn: (data) => {
      const payload = mapFormToBackend(data);
      if (isExistingRecord) {
        return highRiskApi.updateConsent(uhid, payload);
      }
      return highRiskApi.createConsent(payload);
    },
    onSuccess: () => {
      showNotification('success', 'Form submitted successfully');
      clearDraft(DRAFT_KEYS.HIGH_RISK_CONSENT, uhid);
      setIsExistingRecord(true);
      navigate(`/mainPage/${uhid}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: () => showNotification('error', 'Submission failed')
  });

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    
    // Clear error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const handleNameChange = useCallback((e) => {
    const { name, value } = e.target;
    // Basic regex check for names (letters, spaces, dots, hyphens)
    if (value === '' || /^[a-zA-Z\s.-]*$/.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const handleSignatureOpen = (role) => {
    const datetimeField = signatureDatetimeMap[role];
    if (datetimeField && !formData[datetimeField]) {
      setFormData(prev => ({
        ...prev,
        [datetimeField]: new Date().toISOString().slice(0, 16)
      }));
    }
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const base64 = signaturePadRef.current.toDataURL().split(',')[1];
      setFormData(prev => ({ ...prev, [currentSignatureRole]: base64 }));
      setSignatureDialogOpen(false);
    } else {
      showNotification('warning', 'Please provide a signature first');
    }
  };

  const handleShare = async () => {
    try {
      const { token } = await highRiskApi.generateShareToken(uhid);
      const link = `${window.location.origin}/highRiskConsentForm/${uhid}?token=${token}`;
      await navigator.clipboard.writeText(link);
      showNotification('success', 'Secure link copied to clipboard!');
      window.open(`https://wa.me/?text=${encodeURIComponent(`High Risk Consent Link: ${link}`)}`, '_blank');
    } catch (e) {
      showNotification('error', 'Failed to generate share link');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateHighRiskForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showNotification('error', 'Please fill required fields');
      return;
    }
    submitMutation.mutate(formData);
  };

  return {
    formData,
    setFormData,
    errors,
    loading: isInitialLoading || submitMutation.isPending,
    isSubmitting: submitMutation.isPending,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare,
    transcribeAudio: transcriptionMutation.mutate,
    isTranscribing: transcriptionMutation.isPending,
    isExistingRecord
  };
};
